import React from 'react';
import { 
  Box, 
  Typography,
  Grid,
  Card,
  Container,
  Button
} from '@mui/joy';

export type WindowType = {
  id: string;
  name: string;
  imagePath: string;
  previewText: string;
};

// Define window types with placeholder preview text
const windowTypes: WindowType[] = [
  { 
    id: 'casement', 
    name: 'Casement Windows', 
    imagePath: '/images/window-types/casement.jpg',
    previewText: 'Our most popular style with hinged side opening. Multiple configurations available.'
  },
  { 
    id: 'flush-casement', 
    name: 'Flush Sash Windows', 
    imagePath: '/images/window-types/flush-sash.jpg',
    previewText: 'Elegant design with sash sitting flush with the frame. Perfect for period properties.'
  },
  { 
    id: 'tilt-and-turn', 
    name: 'Tilt & Turn Windows', 
    imagePath: '/images/window-types/tilt-and-turn.jpg',
    previewText: 'Modern European style that tilts inward for ventilation or opens fully for cleaning.'
  },
  { 
    id: 'sliding-sash', 
    name: 'Sliding Sash Windows', 
    imagePath: '/images/window-types/sliding-sash.jpg',
    previewText: 'Traditional vertical sliding windows with timeless appeal. Classic or modern designs.'
  },
  { 
    id: 'aluminium', 
    name: 'Aluminium Windows', 
    imagePath: '/images/window-types/aluminium.jpg',
    previewText: 'Contemporary slim-frame aluminum windows with excellent durability and minimal maintenance.'
  },
  {
    id: 'timber',
    name: 'Timber Windows',
    imagePath: '/images/window-types/timber.jpg',
    previewText: 'Traditional timber windows with classic styling. Ideal for heritage and conservation properties.'
  },
];

type WindowTypeSelectorProps = {
  onSelectType: (typeId: string) => void;
  selectedType: string | null;
};

const WindowTypeSelector: React.FC<WindowTypeSelectorProps> = ({ onSelectType, selectedType }) => {
  return (
    <Box>
      <Typography level="h2" sx={{ mb: 3 }}>
        Select Window Type
      </Typography>
      
      <Box sx={{ 
        backgroundColor: '#f2f2f2', 
        borderRadius: '4px', 
        px: { xs: 0.5, sm: 4 }, 
        py: { xs: 2, sm: 4 }, 
        mb: 4 
      }}>
        <Grid container spacing={2}>
          {windowTypes.map((type) => (
            <Grid xs={12} sm={6} md={type.id === 'casement' || type.id === 'flush-casement' || type.id === 'tilt-and-turn' ? 4 : 6} key={type.id}>
              <Card 
                variant="outlined"
                sx={{ 
                  height: '100%',
                  width: { xs: '100%', sm: 'auto' },
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.500',
                    boxShadow: 'sm',
                  },
                  display: 'flex',
                  flexDirection: 'column',
                  border: selectedType === type.id ? '2px solid' : '1px solid',
                  borderColor: selectedType === type.id ? 'primary.500' : 'neutral.200',
                  overflow: 'hidden',
                  backgroundColor: 'white',
                  p: 0
                }}
                onClick={() => onSelectType(type.id)}
              >
                <Box sx={{ p: { xs: 1, sm: 2 }, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Box
                    component="img"
                    src={type.imagePath}
                    alt={type.name}
                    sx={{
                      width: { xs: '100%', sm: '90%' },
                      maxWidth: { xs: '100%', sm: 220 },
                      height: 'auto',
                      maxHeight: { xs: 120, sm: 160 },
                      objectFit: 'contain',
                      transition: 'transform 0.3s ease',
                      transform: selectedType === type.id ? 'scale(1.05)' : 'scale(1)'
                    }}
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                      // Fallback if image fails to load
                      e.currentTarget.style.display = 'none';
                      const parent = e.currentTarget.parentElement;
                      if (parent) {
                        const fallback = document.createElement('div');
                        fallback.style.width = '100%';
                        fallback.style.height = '100%';
                        fallback.style.display = 'flex';
                        fallback.style.justifyContent = 'center';
                        fallback.style.alignItems = 'center';
                        fallback.style.padding = '10px';
                        fallback.textContent = type.previewText;
                        parent.appendChild(fallback);
                      }
                    }}
                  />
                  
                  <Typography 
                    level="title-md" 
                    sx={{ 
                      fontWeight: selectedType === type.id ? 'bold' : 'normal',
                      color: selectedType === type.id ? 'primary.600' : 'text.primary',
                    }}
                  >
                    {type.name}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
        
        {/* OK Button like in the reference image */}
        {selectedType && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 3 }}>
            <Button 
              color="primary" 
              size="md"
              onClick={() => onSelectType(selectedType)}
              sx={{ minWidth: '70px' }}
            >
              OK
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export { windowTypes };
export default WindowTypeSelector; 